/**
 * Recommendation round list.
 *
 * Public, like the rest of the share link: a visitor may read which rounds have
 * been produced even before signing in. The page is a list of entry points, not
 * a result view, so each round is a single link.
 */

import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorBlock, SkeletonCard } from '../../components/layout/states'
import { RoundCard } from '../../components/recommend/round-card'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/primitives'
import { useSharedRounds } from '../../hooks/use-shared'
import { useSharedOutlet } from './shared-layout'

export function RecommendationsPage() {
  const { t } = useTranslation()
  const { shareToken, event } = useSharedOutlet()
  const roundsQuery = useSharedRounds(shareToken)

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: event.title, to: `/e/${shareToken}` },
          { label: t('recommend.title') },
        ]}
        title={t('recommend.title')}
      />

      {roundsQuery.isPending ? (
        <div className="flex flex-col gap-3">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : roundsQuery.isError || !roundsQuery.data ? (
        <ErrorBlock
          title={t('error.internal_error')}
          onRetry={() => void roundsQuery.refetch()}
          retryLabel={t('common.retry')}
        />
      ) : roundsQuery.data.rounds.length === 0 ? (
        <EmptyState
          title={t('recommend.noRounds')}
          description={t('recommend.noRoundsHint')}
          action={
            <Button variant="outline" asChild className="mt-2">
              <Link to={`/e/${shareToken}`}>{t('overview.navOverview')}</Link>
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {roundsQuery.data.rounds.map((round) => (
            <RoundCard key={round.round} shareToken={shareToken} round={round} />
          ))}
        </div>
      )}
    </div>
  )
}
